import { z } from "zod";
import type { AggregateQueryInterval, ApiQuery } from "~/modules/filters/types";
import type { AssetColumns } from "~/modules/assets/types";
import type {
    ChartTypes,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { LeagueTableGroupTypes } from "~/modules/league_tables/types";
import type { NetFlowsScale } from "~/modules/visualization/components/NetFlowsChart/component";

// // // //

export const CHART_TYPES = [
    "timeseries",
    "bar_chart",
    "composition",
    "asset_table",
    "metric",
    "net_flows",
    "league_table",
    "fact_sheet",
] as const;

export enum ChartTypeEnum {
    Timeseries = "timeseries",
    BarChart = "bar_chart",
    Composition = "composition",
    AssetTable = "asset_table",
    Metric = "metric",
    NetFlows = "net_flows",
    LeagueTable = "league_table",
    FactSheet = "fact_sheet",
}

export type ChartType = (typeof CHART_TYPES)[number];

export const CHART_VISIBILITY = ["public", "private", "organization"] as const;

export type ChartVisibility = (typeof CHART_VISIBILITY)[number];

// ENHANCEMENT - add starred/is_favorite boolean field (might need a separate join table between Users <> Charts for favorites) <-- can be a fast follow, not needed for release
// EHNAHCENEMT - rename `curated` field, make it admin-only

/**
 * ChartSchema
 * Runtime validation schema. Mirrors every field on the `DashboardInsight`
 * Prisma model that the client is allowed to see.
 */
export const ChartSchema = z.object({
    id: z.string(),
    slug: z.string(),
    organizationID: z.string(),
    title: z.string(),
    description: z.string(),
    type: z.enum(CHART_TYPES),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdByUserID: z.string(),
    updatedByUserID: z.string().nullable(),
    createdByUserEmail: z.string(),
    updatedByUserEmail: z.string().nullable(),
    views: z.number(),
    published: z.boolean(),
    archived: z.boolean(),
    official: z.boolean(),
    isFavorite: z.boolean(),
    /** Total number of users who have starred this chart. */
    favoriteCount: z.number(),
    visibility: z.enum(CHART_VISIBILITY),
    config: z.record(z.string(), z.any()),
});

/**
 * Chart
 * Chart entity type
 */
export type Chart = z.infer<typeof ChartSchema>;

/**
 * ChartConfig
 * Chart configuration type
 */
export type ChartConfig = Record<string, unknown>;

// // // //

/**
 * LegendPosition
 * Placement of a chart's legend relative to the plot area.
 */
export type LegendPosition = "top" | "right" | "bottom" | "left";

/**
 * TimeseriesChartConfig
 * Config shape for `timeseries` charts.
 */
export interface TimeseriesChartConfig {
    /** Measure plotted on the chart (stored separately from the query). */
    measure_slug: string;
    /** Filter / aggregate / sort / pagination query. */
    query: ApiQuery;
    /** Chart rendering style. Defaults to area when unset. */
    chart_type?: ChartTypes;
    /** Whether to show the chart legend. Defaults to true when unset. */
    legend_enabled?: boolean;
    /** Where the legend sits relative to the plot. Defaults to right. */
    legend_position?: LegendPosition;
    /** Default group-by. */
    default_group_by?: TokenTimeseriesGroupBy;
    /** Group-bys viewers can switch between; the first is the default. */
    groups_by_options?: TokenTimeseriesGroupBy[];
    /** Default interval. */
    default_interval?: AggregateQueryInterval;
    /** Intervals viewers can switch between; the first is the default. */
    intervals_options?: AggregateQueryInterval[];
}

/**
 * CompositionChartType
 * Rendering style for `composition` charts.
 */
export enum CompositionChartType {
    pie = "pie_chart",
    donut = "donut_chart",
}

/**
 * CompositionChartConfig
 * Config shape for `composition` charts.
 */
export interface CompositionChartConfig {
    /** Measure summed into each slice (stored separately from the query). */
    measure_slug: string;
    /** Filter / aggregate / sort / pagination query. */
    query: ApiQuery;
    /** Chart rendering style. Defaults to pie when unset. */
    chart_type?: CompositionChartType;
    /** Whether to show the chart legend. Defaults to true when unset. */
    legend_enabled?: boolean;
    /** Where the legend sits relative to the plot. Defaults to right. */
    legend_position?: LegendPosition;
    /** Maximum number of groups (slices) rendered. Defaults to 25 when unset. */
    group_limit?: number;
    /** Default group-by. */
    default_group_by?: TokenTimeseriesGroupBy;
    /** Group-bys viewers can switch between; the first is the default. */
    groups_by_options?: TokenTimeseriesGroupBy[];
}

/**
 * FlowsChartConfig
 * Config shape for `net_flows` charts.
 */
export interface FlowsChartConfig {
    /** ISO date string for the start of the window. */
    start_date: string;
    /** ISO date string for the end of the window. */
    end_date: string;
    /** Minimum absolute net flow (±) for an asset to be included. */
    min_net_flow: number;
    /** Value-axis scale. */
    scale: NetFlowsScale;
    /** Filter / aggregate / sort / pagination query scoping the flows. */
    query: ApiQuery;
    /** Default group-by. */
    default_group_by?: TokenTimeseriesGroupBy;
    /** Group-bys viewers can switch between; the first is the default. */
    groups_by_options?: TokenTimeseriesGroupBy[];
}

/**
 * LeagueTableChartConfig
 * Config shape for `league_table` charts.
 */
export interface LeagueTableChartConfig {
    /** Measure ranked in each group tab (stored separately from the query). */
    measure_slug: string;
    /** Token aggregates query - only its `filter` scopes the table. */
    query: ApiQuery;
    /** Group types rendered as tabs on the table. */
    groups?: LeagueTableGroupTypes[];
    /** Maximum number of rows shown per group. Defaults to 10 when unset. */
    max_rows?: number;
}

/**
 * AssetTableColumnConfig
 * A single column rendered by an `asset_table` chart. Stored as an object (as
 * opposed to a bare slug) so per-column display options can be added later
 * without migrating existing configs.
 */
export interface AssetTableColumnConfig {
    /** The asset field the column renders. */
    slug: AssetColumns;
    /** The width of the column in pixels */
    width: number;
}

/**
 * AssetTableChartConfig
 * Config shape for `asset_table` charts.
 */
export interface AssetTableChartConfig {
    /** Filter / sort / pagination query for the underlying assets. */
    query: ApiQuery;
    /** Columns rendered in the table, in display order. */
    table_columns: AssetTableColumnConfig[];
}

/**
 * ChartConfigByType
 * Maps each supported chart type to its typed configuration shape.
 */
export interface ChartConfigByType {
    timeseries: TimeseriesChartConfig;
    composition: CompositionChartConfig;
    net_flows: FlowsChartConfig;
    league_table: LeagueTableChartConfig;
    asset_table: AssetTableChartConfig;
}
